import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import Modal from '../../components/Modal';
import withdrawalsService from '../../services/withdrawals.service';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { approveMfaErrorMessage } from '../../lib/mfaApproval';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { pendingAge, sortOldestFirst } from '../../lib/approvalAging';
import { fullName } from '../../lib/fullName';
import { colors, gradients } from '../../styles/theme';
import {
  describeEdgeCountry,
  describePlace,
  describeTakeover,
  edgeCountryTone,
  placeTone,
  takeoverTone,
} from './withdrawals.helpers';
import type {
  WithdrawalChecklist,
  WithdrawalRequest,
  WithdrawalStatus,
} from '../../types/finance';

const STATUS_TONE: Record<WithdrawalStatus, BadgeTone> = {
  requested: 'warning',
  pending: 'info',
  approved: 'success',
  rejected: 'danger',
  reversed: 'neutral',
  cancelled: 'neutral',
};

/** Action-column content for a non-pending request: the rejection reason if
 *  one exists, else an em-dash. Extracted so the action cell stays a single
 *  ternary (pending → buttons, else → this) rather than a nested one. */
function SettledReasonCell({ reason }: Readonly<{ reason: string | null }>): JSX.Element {
  return reason ? (
    <span className="text-xs" style={{ color: colors.muted }}>{reason}</span>
  ) : (
    <span className="text-xs" style={{ color: colors.mutedSoft }}>—</span>
  );
}

type StatusFilter = 'open' | 'all';

/** Whether a row is overdue: a `requested` one by the server's retention-call flag (#340: the
 *  owner's limit in business days, or no agent to call), a `pending` one by the queue's soft SLA. */
export function retentionLate(w: WithdrawalRequest, pendingStale: boolean): boolean {
  if (w.status === 'requested') return w.retention_overdue === true;
  return w.status === 'pending' && pendingStale;
}
type RetentionAction = 'escalate' | 'cancel';

/** The roles that work each step of a client's withdrawal (the API's own: escalate / cancel take an agent or a
 *  super_admin, approve / reject a manager or a super_admin). */
const RETAINS = new Set(['agent', 'super_admin']);
const APPROVES = new Set(['manager', 'super_admin']);

/**
 * One screen for the whole flow of a client's withdrawal, whoever works it: a request is born `requested`, the
 * retention call escalates it to `pending` (or the client keeps the investment and it is cancelled), and a manager
 * approves or rejects what is pending. Each person sees the actions their role may take, so a request never waits on
 * an account of one particular role. Served at /agent/withdrawals and /manager/withdrawals.
 */
export default function StaffWithdrawals(): JSX.Element {
  const role = authService.getRole() ?? '';
  const mayRetain = RETAINS.has(role);
  const mayApprove = APPROVES.has(role);
  const clientsPath = role === 'agent' ? '/agent/clients' : '/manager/clients';
  const [filter, setFilter] = useState<StatusFilter>('open');
  const [retention, setRetention] = useState<{ w: WithdrawalRequest; kind: RetentionAction } | null>(null);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [slaDays, setSlaDays] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<WithdrawalRequest | null>(null);
  const [approveTarget, setApproveTarget] = useState<WithdrawalRequest | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await withdrawalsService.list({
        status: filter === 'open' ? ['requested', 'pending'] : undefined,
        size: 100,
      });
      setWithdrawals(res.items);
      setSlaDays(res.retention_call_sla_business_days ?? null);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load withdrawals.'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  let body: JSX.Element;
  if (loading) {
    body = <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>;
  } else if (withdrawals.length === 0) {
    body = (
      <div
        className="rounded-2xl p-8 text-center text-sm"
        style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
      >
        {filter === 'open'
          ? 'No withdrawal is waiting.'
          : 'No withdrawals to display.'}
      </div>
    );
  } else {
    body = (
      <div
        className="overflow-hidden rounded-2xl"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <table className="w-full text-sm">
          <thead style={{ background: colors.canvas, color: colors.muted }}>
            <tr>
              <th className="text-left font-medium px-4 py-3">Member</th>
              <th className="text-left font-medium px-4 py-3">Product</th>
              <th className="text-right font-medium px-4 py-3">Amount</th>
              <th className="text-left font-medium px-4 py-3">Reference</th>
              <th className="text-left font-medium px-4 py-3">Proposed by</th>
              <th className="text-left font-medium px-4 py-3">Status</th>
              <th className="text-right font-medium px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {(filter === 'open' ? sortOldestFirst(withdrawals) : withdrawals).map((w, idx) => {
              const waiting = w.status === 'pending' || w.status === 'requested';
              const age = waiting ? pendingAge(w.created_at) : null;
              const late = retentionLate(w, age?.isStale ?? false);
              return (
              <tr
                key={w.id}
                style={{
                  borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}`,
                  background: late ? colors.warningBg : undefined,
                }}
              >
                <td className="px-4 py-3">
                  <Link
                    to={`${clientsPath}/${w.user_id}`}
                    state={{ clientName: fullName({ first_name: w.user_first_name, last_name: w.user_last_name }), clientEmail: w.user_email }}
                    className="font-medium hover:underline"
                    style={{ color: colors.ink }}
                  >
                    {fullName({ first_name: w.user_first_name, last_name: w.user_last_name })}
                  </Link>
                  <div className="text-xs" style={{ color: colors.mutedSoft }}>{w.user_email}</div>
                </td>
                <td className="px-4 py-3" style={{ color: colors.inkSoft }}>{w.product_name}</td>
                <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>
                  {formatMoney(w.amount, w.currency_code)}
                </td>
                <td className="px-4 py-3 font-mono text-xs" style={{ color: colors.muted }}>
                  {w.reference_number ?? '—'}
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                  <div>{w.proposed_by_email ?? '—'}</div>
                  <div>{formatDateTime(w.created_at)}</div>
                  {age && (
                    <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2, color: late ? colors.warning : colors.mutedSoft }}>
                      waiting {age.label}{late ? ' · overdue' : ''}
                    </div>
                  )}
                  {w.status === 'requested' && w.without_agent && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: colors.warning }}>no agent assigned</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge tone={STATUS_TONE[w.status]}>{w.status}</StatusBadge>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <ActionCell
                    w={w}
                    mayRetain={mayRetain}
                    mayApprove={mayApprove}
                    onRetain={(kind) => setRetention({ w, kind })}
                    onApprove={() => setApproveTarget(w)}
                    onReject={() => setRejectTarget(w)}
                  />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Withdrawals"
        subtitle="A client's request waits for the retention call: escalate it if the client still wants the money, or mark it kept. An escalated request waits for a manager, who verifies the destination account and approves — approval posts a permanent debit; the ledger is append-only, so a mistake is fixed with a reversal, never an edit."
        actions={
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
            className="px-3 py-1.5 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, background: colors.surface }}
          >
            <option value="open">Waiting (requested or pending)</option>
            <option value="all">All</option>
          </select>
        }
      />

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{
            background: colors.successBg,
            border: `1px solid ${colors.successBorder}`,
            color: colors.successText,
          }}
        >
          {flash}
        </div>
      )}

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      {slaDays !== null && (
        <p className="mb-3 text-xs" style={{ color: colors.muted }}>
          {`A requested withdrawal is overdue after ${slaDays} business days without the retention call, and at once when the client has no agent; it then also appears in the manager's queue.`}
        </p>
      )}

      {body}

      {approveTarget && (
        <ApproveChecklistModal
          target={approveTarget}
          onClose={() => setApproveTarget(null)}
          onSubmitted={(msg) => {
            setApproveTarget(null);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 6000);
            reload();
          }}
        />
      )}

      {retention && (
        <RetentionModal
          target={retention.w}
          kind={retention.kind}
          onClose={() => setRetention(null)}
          onSubmitted={(msg) => {
            setRetention(null);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 6000);
            reload();
          }}
        />
      )}

      {rejectTarget && (
        <RejectModal
          target={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onSubmitted={(msg) => {
            setRejectTarget(null);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 5000);
            reload();
          }}
        />
      )}
    </>
  );
}


interface ActionCellProps {
  readonly w: WithdrawalRequest;
  readonly mayRetain: boolean;
  readonly mayApprove: boolean;
  readonly onRetain: (kind: RetentionAction) => void;
  readonly onApprove: () => void;
  readonly onReject: () => void;
}

/** What this person can do with one request at its step; a step that is not theirs says who it waits for. */
export function ActionCell({ w, mayRetain, mayApprove, onRetain, onApprove, onReject }: ActionCellProps): JSX.Element {
  if (w.status === 'requested') {
    if (!mayRetain) {
      return <span className="text-xs" style={{ color: colors.muted }}>Waiting for the retention call</span>;
    }
    return (
      <span className="inline-flex gap-2">
        <button
          type="button"
          onClick={() => onRetain('escalate')}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{ background: gradients.gold, color: colors.navy }}
        >
          Escalate
        </button>
        <button
          type="button"
          onClick={() => onRetain('cancel')}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Client kept it
        </button>
      </span>
    );
  }
  if (w.status === 'pending') {
    if (!mayApprove) {
      return <span className="text-xs" style={{ color: colors.muted }}>Waiting for a manager</span>;
    }
    return (
      <span className="inline-flex gap-2">
        <button
          type="button"
          onClick={onApprove}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{ background: gradients.gold, color: colors.navy }}
        >
          Review &amp; approve
        </button>
        <button
          type="button"
          onClick={onReject}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}
        >
          Reject
        </button>
      </span>
    );
  }
  return <SettledReasonCell reason={w.rejection_reason} />;
}

interface ApproveProps {
  target: WithdrawalRequest;
  onClose: () => void;
  onSubmitted: (msg: string) => void;
}

/** Row in the manager checklist: a computed signal + a tone. */
const _CHECK_TONE: Record<'ok' | 'warn' | 'muted', string> = {
  ok: colors.success,
  warn: colors.danger,
  muted: colors.inkSoft,
};

/** Human label for the advisory name-match signal (null = couldn't compute). */
function nameMatchLabel(match: boolean | null): string {
  if (match === null) return 'Unknown';
  return match ? 'Yes' : 'No — review';
}

function ChecklistRow(
  { label, value, tone }: Readonly<{ label: string; value: string; tone: 'ok' | 'warn' | 'muted' }>,
): JSX.Element {
  const color = _CHECK_TONE[tone];
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-xs" style={{ color: colors.muted }}>{label}</span>
      <span className="text-sm font-medium text-right" style={{ color }}>{value}</span>
    </div>
  );
}

/** Surrender-charge preview rows. Hidden for product types that carry no
 *  penalty (penalty_method === null). The rate/amount is the one that applies "today"
 *  and is frozen onto the request when the manager approves. */
function PenaltySummary({ checklist }: Readonly<{ checklist: WithdrawalChecklist }>): JSX.Element | null {
  if (checklist.penalty_method === null) return null;
  const pct = Number(checklist.penalty_pct);
  const ccy = checklist.currency_code;
  return (
    <>
      <ChecklistRow
        label="Early-withdrawal penalty"
        value={pct > 0
          ? `${(pct * 100).toFixed(2)}% · ${formatMoney(checklist.penalty_amount, ccy)}`
          : 'None (past the surrender schedule)'}
        tone={pct > 0 ? 'warn' : 'ok'}
      />
      <ChecklistRow
        label="Net to client"
        value={formatMoney(checklist.net_amount, ccy)}
        tone="muted"
      />
      {checklist.acknowledged_penalty_amount !== null && (
        <ChecklistRow
          label="Charge accepted by client"
          value={formatMoney(checklist.acknowledged_penalty_amount, ccy)}
          tone={checklist.penalty_reacknowledgement_required ? 'warn' : 'ok'}
        />
      )}
    </>
  );
}

function ApproveChecklistModal({ target, onClose, onSubmitted }: Readonly<ApproveProps>): JSX.Element {
  const [checklist, setChecklist] = useState<WithdrawalChecklist | null>(null);
  const [ownershipAck, setOwnershipAck] = useState(false);
  const [changeAck, setChangeAck] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    withdrawalsService.getChecklist(target.id)
      .then((c) => { if (!cancelled) setChecklist(c); })
      .catch((e: unknown) => {
        if (!cancelled) setLoadErr(getApiErrorMessage(e, 'Could not load the checklist.'));
      });
    return () => { cancelled = true; };
  }, [target.id]);

  const blockedByCoolingOff = checklist?.within_cooling_off ?? false;
  // The charge has risen above what the client accepted. A manager cannot consent on
  // the client's behalf to a bigger charge, so this is a hard blocker like the
  // cooling-off one — the server refuses the approval either way, and hiding that
  // until the 422 lands would just waste the manager's step-up code.
  const blockedByCharge = checklist?.penalty_reacknowledgement_required ?? false;
  const canApprove =
    ownershipAck && changeAck && !blockedByCoolingOff && !blockedByCharge
    && mfaCode.trim().length >= 6 && !busy;
  const memberName = fullName({ first_name: target.user_first_name, last_name: target.user_last_name });

  const submit = async (): Promise<void> => {
    if (!canApprove) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await withdrawalsService.approve(target.id, target.version, {
        account_ownership_reviewed: ownershipAck,
        account_change_reviewed: changeAck,
      }, mfaCode.trim());
      onSubmitted(`Approved. New balance for ${memberName}: ${formatMoney(res.new_balance, target.currency_code)}.`);
    } catch (e: unknown) {
      setErr(approveMfaErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Review &amp; approve withdrawal
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Verify the destination account before releasing {formatMoney(target.amount, target.currency_code)} for {memberName}.
        Approving posts a permanent debit.
      </p>

      {loadErr && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {loadErr}
        </div>
      )}

      {!checklist && !loadErr && (
        <p className="text-sm" style={{ color: colors.muted }}>Loading checklist…</p>
      )}

      {checklist && (
        <div className="rounded-lg p-4 mb-4" style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}>
          <ChecklistRow
            label="Client of record"
            value={checklist.client_legal_name || '—'}
            tone="muted"
          />
          <ChecklistRow
            label="Destination account"
            value={checklist.has_destination_account
              ? `${checklist.destination_holder_name ?? '—'} · ····${checklist.destination_account_last4 ?? ''}`
              : 'None on file'}
            tone={checklist.has_destination_account ? 'muted' : 'warn'}
          />
          <ChecklistRow
            label="Name matches client"
            value={nameMatchLabel(checklist.name_match)}
            tone={checklist.name_match ? 'ok' : 'warn'}
          />
          <ChecklistRow
            label="Account added"
            value={checklist.business_days_since_account_added === null
              ? '—'
              : `${checklist.business_days_since_account_added} business day(s) ago`}
            tone={blockedByCoolingOff ? 'warn' : 'ok'}
          />
          {blockedByCoolingOff && (
            <div className="mt-2 p-2 rounded-md text-xs"
                 style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
              Within the 3-business-day cooling-off. This account can receive a payout on or after{' '}
              {checklist.cooling_off_clears_on ?? '—'}. Approval is blocked until then.
            </div>
          )}
          <ChecklistRow
            label="Requested from"
            value={describePlace(checklist)}
            tone={placeTone(checklist)}
          />
          {/* Directly under the browser's line, because the two answer the same
              question from different sources and a reviewer should read them as a
              pair: the browser reading is consent-based and spoofable, this one is
              the load balancer's and the client cannot set it. Neither blocks. */}
          <ChecklistRow
            label="Connected from"
            value={describeEdgeCountry(checklist)}
            tone={edgeCountryTone(checklist)}
          />
          {/* Third and last of the location-and-behaviour lines, and the only one that
              looks at a sequence rather than this single request. Reads as silence until
              the client has a baseline. Blocks nothing. */}
          <ChecklistRow
            label="Recent sign-ins"
            value={describeTakeover(checklist)}
            tone={takeoverTone(checklist)}
          />
          {/* 0113 (#157): the commitment the manager used to never see. Warn
              tone while it lies in the future — the server refuses an
              early-and-free approval either way; this stops the wasted
              step-up code. Display metadata: funds re-verifies at intake. */}
          {target.funds_eligible_at && (
            <ChecklistRow
              label="Can be paid from"
              value={formatDateTime(target.funds_eligible_at)}
              tone={new Date(target.funds_eligible_at) > new Date() ? 'warn' : 'ok'}
            />
          )}
          <PenaltySummary checklist={checklist} />
          {blockedByCharge && (
            <div className="mt-2 p-2 rounded-md text-xs"
                 style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
              The early-withdrawal charge is now{' '}
              {formatMoney(checklist.penalty_amount, checklist.currency_code)}, more than the
              client accepted
              {checklist.acknowledged_penalty_amount === null
                ? ''
                : ` (${formatMoney(checklist.acknowledged_penalty_amount, checklist.currency_code)})`}
              . Approval is blocked: ask the client to submit a new withdrawal accepting the
              current charge.
            </div>
          )}
        </div>
      )}

      <label className="flex items-start gap-2 mb-2 text-sm" style={{ color: colors.inkSoft }}>
        <input type="checkbox" checked={ownershipAck} disabled={busy}
               onChange={(e) => setOwnershipAck(e.target.checked)} className="mt-0.5" />
        <span>I confirm the destination account belongs to the client (name verified).</span>
      </label>
      <label className="flex items-start gap-2 mb-4 text-sm" style={{ color: colors.inkSoft }}>
        <input type="checkbox" checked={changeAck} disabled={busy}
               onChange={(e) => setChangeAck(e.target.checked)} className="mt-0.5" />
        <span>I reviewed when the destination account was added and found no anomaly.</span>
      </label>

      <div className="mb-4">
        <label htmlFor="wd-approve-mfa" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
          Authentication code
        </label>
        <input
          id="wd-approve-mfa"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
          placeholder="6-digit code"
          disabled={busy}
          className="w-full px-3 py-2 rounded-lg text-sm font-mono tracking-widest"
          style={{ border: `1px solid ${colors.border}` }}
        />
        <p className="text-xs mt-1" style={{ color: colors.muted }}>
          From your authenticator app. Approving a payout requires two-factor authentication.
        </p>
      </div>

      {err && (
        <div className="mb-3 p-3 rounded-lg text-sm"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}>
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={!canApprove}
                className="px-4 py-2 rounded-lg text-sm font-semibold"
                style={{
                  background: canApprove ? gradients.gold : colors.disabledBg,
                  color: canApprove ? colors.navy : colors.disabledText,
                }}>
          {busy ? 'Approving…' : 'Approve withdrawal'}
        </button>
      </div>
    </Modal>
  );
}

interface RejectProps {
  target: WithdrawalRequest;
  onClose: () => void;
  onSubmitted: (msg: string) => void;
}

function RejectModal({ target, onClose, onSubmitted }: Readonly<RejectProps>): JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const trimmed = reason.trim();
  const valid = trimmed.length >= 5 && trimmed.length <= 1000;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      await withdrawalsService.reject(target.id, target.version, trimmed);
      onSubmitted(`Rejected ${formatMoney(target.amount, target.currency_code)} for ${fullName({ first_name: target.user_first_name, last_name: target.user_last_name })}.`);
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not reject.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Reject withdrawal
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Reason is required and stored in the audit log. The proposing agent
        sees it; the member does not see it verbatim.
      </p>
      <div
        className="mb-4 p-3 rounded-lg text-xs"
        style={{ background: colors.surfaceMuted, color: colors.textPrimary }}
      >
        <div className="font-semibold">{fullName({ first_name: target.user_first_name, last_name: target.user_last_name })}</div>
        <div style={{ color: colors.textMuted }}>
          {formatMoney(target.amount, target.currency_code)} · ref{' '}
          {target.reference_number ?? '—'}
        </div>
      </div>

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={4}
        maxLength={1000}
        placeholder="e.g. Insufficient balance at time of approval."
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={{ border: `1px solid ${colors.border}` }}
      />
      <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
        {trimmed.length}/1000 · minimum 5 characters
      </div>

      {err && (
        <div
          className="mt-3 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !valid}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: busy || !valid ? colors.disabledBg : colors.danger,
            color: busy || !valid ? colors.disabledText : '#FFFFFF',
          }}
        >
          {busy ? 'Rejecting…' : 'Reject'}
        </button>
      </div>
    </Modal>
  );
}

interface RetentionProps {
  readonly target: WithdrawalRequest;
  readonly kind: RetentionAction;
  readonly onClose: () => void;
  readonly onSubmitted: (msg: string) => void;
}

function RetentionModal({ target, kind, onClose, onSubmitted }: RetentionProps): JSX.Element {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const memberName = fullName({ first_name: target.user_first_name, last_name: target.user_last_name });
  const isEscalate = kind === 'escalate';
  let submitLabel: string;
  if (busy) {
    submitLabel = 'Working…';
  } else {
    submitLabel = isEscalate ? 'Escalate' : 'Mark as kept';
  }

  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      if (isEscalate) {
        await withdrawalsService.escalate(target.id, target.version, text.trim() || undefined);
        onSubmitted(`Escalated ${formatMoney(target.amount, target.currency_code)} for ${memberName} to a manager.`);
      } else {
        await withdrawalsService.cancel(target.id, target.version, text.trim() || undefined);
        onSubmitted(`Marked ${memberName}'s withdrawal as kept (cancelled).`);
      }
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not complete the action.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        {isEscalate ? 'Escalate to manager' : 'Client kept the investment'}
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        {isEscalate
          ? `Retention call did not change the client's decision. Escalate ${formatMoney(target.amount, target.currency_code)} for ${memberName} so a manager can review the destination account and approve.`
          : `The client decided to keep the investment. This cancels their ${formatMoney(target.amount, target.currency_code)} request — they can submit a new one any time.`}
      </p>

      <label className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        {isEscalate ? 'Note for the manager (optional)' : 'Retention note (optional)'}
      </label>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={busy}
        rows={3}
        maxLength={2000}
        placeholder={isEscalate ? 'e.g. Client needs the funds for a property closing.' : 'e.g. Client agreed to stay after we reviewed when their term ends.'}
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={{ border: `1px solid ${colors.border}` }}
      />

      {err && (
        <div className="mt-3 p-3 rounded-lg text-sm"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onClose} disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}>
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-semibold"
                style={{
                  background: isEscalate ? gradients.gold : colors.danger,
                  color: isEscalate ? colors.navy : '#FFFFFF',
                }}>
          {submitLabel}
        </button>
      </div>
    </Modal>
  );
}
